<template>
  <div>
    <a-modal
      :visible="visible"
      :title="title || $t('SKU快捷查询')"
      :confirm-loading="confirmLoading"
      width="900px"
      :dialog-style="{ top: '20px' }"
      @cancel="handleCancel"
      :footer="null"
      :maskClosable="false"
    >
      <a-form-model :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-row v-if="recentPairs.length" style="margin-bottom: 20px">
          <a-col :span="24">
            <div class="recent-wrapper" style="display: flex; align-items: center; flex-wrap: wrap">
              <div class="recent-label">{{ $t('历史选择') }}：</div>
              <div class="recent-tags">
                <a-tag
                  v-for="item in recentPairs"
                  :key="item.key"
                  closable
                  @close="(e) => handleRemoveRecent(e, item.key)"
                  @click="() => applyRecent(item)"
                >
                  {{ item.brandName }} / {{ item.modelName }}
                </a-tag>
              </div>
            </div>
          </a-col>
        </a-row>

        <a-row :gutter="16" style="margin-top: 20px">
          <a-col :span="12">
            <a-form-model-item>
              <template slot="label">
                {{ $t('品牌') }}
                <a-icon
                  type="sync"
                  class="text-link"
                  style="margin-left: 4px; padding-right: 4px"
                  :spin="brandLoading"
                  @click="handleRefreshBrandList"
                />
              </template>

              <a-spin :spinning="brandLoading" size="small">
                <div class="tag-group tag-group-scroll">
                  <span v-if="!brandOptions.length" style="color: rgba(0, 0, 0, 0.45)">
                    {{ $t('暂无品牌数据') }}
                  </span>
                  <a-checkable-tag
                    class="cursor-pointer"
                    v-for="item in brandOptions"
                    :key="item.value"
                    :checked="form.brandCode === item.value"
                    :disabled="brandLoading"
                    @change="(checked) => handleBrandTagChange(checked, item.value)"
                  >
                    {{ item.label }}
                  </a-checkable-tag>
                </div>
              </a-spin>
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('型号')">
              <a-select
                style="width: 100%"
                v-model="form.modelCode"
                :placeholder="$t('请先选品牌，再选择型号')"
                allow-clear
                show-search
                :disabled="!form.brandCode"
                :filterOption="
                  (input, option) =>
                    (option.componentOptions?.children?.[0]?.text || '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                "
                @change="handleModelChange"
              >
                <a-select-option v-for="item in modelOptions" :key="item.value" :value="item.value">
                  {{ item.label }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
        </a-row>

        <a-tabs v-model="activeTab" style="margin-top: 12px" @change="updateMatchHint">
          <a-tab-pane key="precise" :tab="$t('精准查询')">
            <a-row
              v-if="specFields.length"
              :gutter="16"
              style="margin-top: 8px; display: flex; flex-wrap: wrap; gap: 20px 0"
            >
              <a-col v-for="f in specFields" :key="f.styleType" :span="12">
                <a-form-model-item :label="f.styleType">
                  <div class="tag-group">
                    <a-checkable-tag
                      v-for="v in f.styleValues"
                      class="cursor-pointer"
                      :key="`${f.styleType}:${v}`"
                      :checked="selectedSpecs?.[f.styleType] === v"
                      @change="(checked) => handleSpecTagChange(checked, f.styleType, v)"
                    >
                      {{ v }}
                    </a-checkable-tag>
                  </div>
                </a-form-model-item>
              </a-col>
            </a-row>
          </a-tab-pane>

          <a-tab-pane key="fuzzy" :tab="$t('模糊查询')" :disabled="!skuResult">
            <div style="margin-bottom: 12px; color: #1890ff; font-size: 13px">
              <a-icon type="info-circle" style="margin-right: 4px" />
              {{ $t('使用说明：想要查询什么规格值就选定哪些值（支持多选）') }}
            </div>
            <a-row :gutter="16" style="display: flex; flex-wrap: wrap; gap: 20px 0">
              <a-col v-for="f in fuzzySpecFields" :key="f.styleType" :span="12">
                <a-form-model-item :label="f.styleType">
                  <div class="tag-group">
                    <a-checkable-tag
                      v-for="v in f.styleValues"
                      class="cursor-pointer"
                      :key="`fuzzy:${f.styleType}:${v}`"
                      :checked="(fuzzySelectedSpecs[f.styleType] || []).includes(v)"
                      @change="(checked) => handleFuzzySpecTagChange(checked, f.styleType, v)"
                    >
                      {{ v }}
                    </a-checkable-tag>
                  </div>
                </a-form-model-item>
              </a-col>
            </a-row>
          </a-tab-pane>
        </a-tabs>

        <div v-if="matchHint" style="margin-top: 10px; color: rgba(0, 0, 0, 0.65)">
          {{ matchHint }}
        </div>

        <a-row style="margin-top: 20px">
          <a-col :span="24" style="text-align: right">
            <a-button @click="handleCancel">{{ $t('取消') }}</a-button>
            <a-button
              v-if="activeTab === 'fuzzy'"
              type="primary"
              style="margin-left: 8px"
              :disabled="!fuzzyCanQuery || confirmLoading"
              @click="queryFuzzyStockList"
            >
              {{ $t('模糊查询') }}
            </a-button>
            <a-button
              v-if="activeTab === 'precise'"
              type="primary"
              style="margin-left: 8px"
              :disabled="!canSubmit"
              @click="emitIfReady(true)"
            >
              {{ $t('确定') }}
            </a-button>
          </a-col>
        </a-row>
      </a-form-model>
    </a-modal>

    <a-modal
      :visible="fuzzyStockModalVisible"
      :title="$t('模糊匹配结果')"
      :footer="null"
      width="980px"
      :dialog-style="{ top: '20px' }"
      :maskClosable="false"
      @cancel="handleFuzzyStockCancel"
    >
      <div
        v-if="fuzzyStockList.length"
        style="margin-bottom: 8px; font-size: 14px; font-weight: 500"
      >
        <span style="color: rgba(0, 0, 0, 0.65)">仓库：</span>
        <span>{{ warehouseName }}</span>
      </div>
      <div style="display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 12px">
        <div>
          <span style="color: rgba(0, 0, 0, 0.65)">{{ '总实际库存' }}：</span>
          <span>{{ fuzzyStockTotals.totalRealQuantity ?? 0 }}</span>
        </div>
        <div>
          <span style="color: rgba(0, 0, 0, 0.65)">{{ '总可用数' }}：</span>
          <span>{{ fuzzyStockTotals.totalAvailableQuantity ?? 0 }}</span>
        </div>
        <div>
          <span style="color: rgba(0, 0, 0, 0.65)">{{ '总预定订单' }}：</span>
          <span>{{ fuzzyStockTotals.totalReserveQuantity ?? 0 }}</span>
        </div>
      </div>

      <a-table
        rowKey="inventoryItemId"
        :columns="fuzzyStockColumns"
        :dataSource="fuzzyStockList"
        :loading="confirmLoading"
        :pagination="false"
        :rowSelection="fuzzyStockRowSelection"
        style="margin-top: 8px"
      />
      <div style="text-align: right; margin-top: 12px">
        <Pagination
          :offset="fuzzySearchData.offset"
          :length="fuzzySearchData.length"
          :total-count="fuzzySearchData.total"
          @paginate="paginate"
        />
      </div>
      <div style="text-align: right; margin-top: 12px">
        <a-button @click="handleFuzzyStockCancel">{{ $t('取消') }}</a-button>
        <a-button
          type="primary"
          style="margin-left: 8px"
          :disabled="!fuzzySelectedStockItem"
          @click="confirmFuzzySelection"
        >
          {{ $t('确定') }}
        </a-button>
      </div>
    </a-modal>
  </div>
</template>

<script>
import api from '@/service/api'
import http from '@/service/axios'
import Pagination from '@component/pagination'

// 仅作为“优先展示/排序”的规格集合；最终必选项以接口返回 specOptions 为准
const PREFERRED_STYLE_TYPES = ['颜色', '运行内存', '机身存储', '成新']
const RECENT_CACHE_KEY = 'scm_sku_quick_query_recent_pairs_v1'
const BRAND_CACHE_KEY = 'scm_sku_quick_query_brand_options_v1'
const RECENT_MAX = 12

export default {
  name: 'SkuQuickQueryModal',
  props: {
    title: {
      type: String,
      default: '',
    },
    warehouseCode: {
      type: String,
      default: '',
    },
    warehouseName: {
      type: String,
      default: '',
    },
  },
  components: { Pagination },
  data() {
    return {
      visible: false,
      confirmLoading: false,
      brandLoading: false,
      brandOptions: [],
      modelOptions: [],
      form: {
        brandCode: undefined,
        modelCode: undefined,
      },
      selectedSpecs: {},
      skuResult: null,
      matchHint: '',
      recentPairs: [],
      // 模糊查询结果二次选择
      fuzzyStockModalVisible: false,
      fuzzyStockResult: {},
      fuzzyStockSelectedRowKeys: [],
      fuzzySelectedStockItem: null,
      fuzzySelectedSpecs: {},
      activeTab: 'precise',
      fuzzySearchData: {
        offset: 0,
        length: 10,
        total: 0,
      },
    }
  },
  computed: {
    selectedBrandRaw() {
      return this.brandOptions.find((b) => b.value === this.form.brandCode)?.raw || null
    },
    selectedModelRaw() {
      return this.modelOptions.find((m) => m.value === this.form.modelCode)?.raw || null
    },
    specFields() {
      const specOptions = this.skuResult?.specOptions || []
      const filtered = specOptions
        .filter((o) => PREFERRED_STYLE_TYPES.includes(o.styleType))
        .map((o) => ({
          styleType: o.styleType,
          styleValues: Array.isArray(o.styleValues) ? o.styleValues : [],
        }))
      // 固定顺序
      return PREFERRED_STYLE_TYPES.map((t) => filtered.find((x) => x.styleType === t)).filter(
        Boolean
      )
    },
    requiredStyleTypes() {
      // 以接口 specOptions 实际返回为准（避免某些型号没有“运行内存”等字段）
      return (this.specFields || []).map((x) => x.styleType)
    },
    canSubmit() {
      if (!this.skuResult) return false
      return this.requiredStyleTypes.every((t) => !!this.selectedSpecs?.[t])
    },
    hasAnyRequiredSelected() {
      if (!this.requiredStyleTypes?.length) return false
      return this.requiredStyleTypes.some((t) => !!this.selectedSpecs?.[t])
    },
    fuzzyCanQuery() {
      // 规则：只要有 SKU 数据且在模糊查询 Tab 即可进行模糊查询
      return !!this.skuResult
    },
    // 当前匹配模式：根据当前选中的 Tab 决定
    matchMode() {
      return this.activeTab
    },
    // 模糊查询使用的规格字段（与精确查询相同，但选择方式为多选）
    fuzzySpecFields() {
      return this.specFields
    },
    fuzzyStockTotals() {
      return this.fuzzyStockResult || {}
    },
    fuzzyStockList() {
      return this.fuzzyStockResult?.list || []
    },
    fuzzyStockRowSelection() {
      return {
        type: 'radio',
        selectedRowKeys: this.fuzzyStockSelectedRowKeys,
        onChange: (selectedRowKeys, selectedRows) => {
          this.fuzzyStockSelectedRowKeys = selectedRowKeys
          this.fuzzySelectedStockItem = selectedRows?.[0] || null
        },
      }
    },
    fuzzyStockColumns() {
      return [
        { title: this.$t('sku名称'), dataIndex: 'skuName', key: 'skuName' },
        {
          title: this.$t('规格'),
          key: 'styleMap',
          customRender: (text, record) => {
            const map = record?.styleMap || {}
            // 排除“版本”
            const rest = { ...map }
            delete rest.版本
            return Object.keys(rest)
              .filter((k) => rest[k] !== undefined && rest[k] !== null && rest[k] !== '')
              .map((k) => `${rest[k]}`)
              .join(';')
          },
        },
        { title: this.$t('可用数'), dataIndex: 'availableQuantity', key: 'availableQuantity' },
        { title: this.$t('实际库存'), dataIndex: 'realQuantity', key: 'realQuantity' },
        { title: this.$t('预定订单'), dataIndex: 'reserveQuantity', key: 'reserveQuantity' },
        { title: '批次', dataIndex: 'batchCode', key: 'batchCode' },
      ]
    },
  },
  methods: {
    open({ brandCode, modelCode } = {}) {
      this.resetAll()
      this.visible = true
      this.loadRecentPairs()
      this.fetchBrandList().then(() => {
        if (brandCode) {
          this.form.brandCode = brandCode
          this.handleBrandChange(brandCode)
        }
        if (modelCode) {
          this.form.modelCode = modelCode
          this.handleModelChange(modelCode)
        }
      })
    },
    handleCancel() {
      this.visible = false
      this.resetFuzzyStockState()
      this.$emit('cancel')
    },
    resetAll() {
      this.confirmLoading = false
      this.brandLoading = false
      this.brandOptions = []
      this.modelOptions = []
      this.form = { brandCode: undefined, modelCode: undefined }
      this.selectedSpecs = {}
      this.skuResult = null
      this.matchHint = ''
      this.resetFuzzyStockState()
      this.activeTab = 'precise'
      // recentPairs 不在这里清空，避免 modal 内操作时闪烁
    },
    resetSpecs() {
      this.selectedSpecs = {}
      this.skuResult = null
      this.matchHint = ''
      this.resetFuzzyStockState()
      this.activeTab = 'precise'
    },
    resetFuzzyStockState() {
      this.fuzzyStockModalVisible = false
      this.fuzzyStockResult = {}
      this.fuzzyStockSelectedRowKeys = []
      this.fuzzySelectedStockItem = null
      this.fuzzySearchData = { offset: 0, length: 10 }
    },
    fetchBrandList() {
      return this.fetchBrandListWithCache()
    },
    parseBrandListToOptions(brandList) {
      return (brandList || []).map((b) => ({
        label: b.brandName,
        value: b.brandId,
        raw: b,
      }))
    },
    getBrandListFromCache() {
      try {
        const raw = localStorage.getItem(BRAND_CACHE_KEY)
        const brandList = raw ? JSON.parse(raw) : []
        return Array.isArray(brandList) ? brandList : []
      } catch (e) {
        return []
      }
    },
    setBrandListCache(brandList) {
      try {
        localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(brandList || []))
      } catch (e) {
        // ignore
      }
    },
    fetchBrandListWithCache({ forceRefresh = false } = {}) {
      if (!forceRefresh) {
        const cache = this.getBrandListFromCache()
        if (cache.length) {
          this.brandOptions = this.parseBrandListToOptions(cache)
          return Promise.resolve()
        }
      }
      this.brandLoading = true
      return new Promise((resolve) => {
        http({
          type: 'post',
          url: api.getUPDBrandList,
          data: {},
          hasLoading: false,
          success: (res) => {
            const brandList = res?.result?.brandList || []
            this.brandOptions = this.parseBrandListToOptions(brandList)
            this.setBrandListCache(brandList)
            this.brandLoading = false
            resolve()
          },
          fail: () => {
            this.brandLoading = false
            resolve()
          },
        })
      })
    },
    handleRefreshBrandList() {
      this.fetchBrandListWithCache({ forceRefresh: true })
    },
    fetchModelListByBrandCode(brandCode) {
      const brandRaw = this.brandOptions.find((b) => b.value === brandCode)?.raw
      const models = brandRaw?.brandModelList || []
      this.modelOptions = models.map((m) => ({
        label: m.modelName,
        value: m.modelCode,
        raw: m,
      }))
    },
    handleBrandChange(brandCode) {
      this.form.modelCode = undefined
      this.modelOptions = []
      this.resetSpecs()
      if (!brandCode) return
      this.fetchModelListByBrandCode(brandCode)
    },
    handleBrandTagChange(checked, brandCode) {
      // 单选：checked=false 代表取消选中
      const next = checked ? brandCode : undefined
      this.form.brandCode = next
      this.handleBrandChange(next)
    },
    handleModelChange(modelCode) {
      this.resetSpecs()
      if (!modelCode) return
      const modelName =
        this.modelOptions.find((m) => m.value === modelCode)?.raw?.modelName ||
        this.modelOptions.find((m) => m.value === modelCode)?.label
      if (!modelName) return
      this.querySkuRelationMap(modelCode, modelName)
    },
    querySkuRelationMap(modelCode, modelName) {
      this.confirmLoading = true
      http({
        type: 'post',
        url: api.querySkuRelationMap,
        data: { modelCode, modelName },
        hasLoading: false,
        success: (res) => {
          this.skuResult = res?.result || null
          this.confirmLoading = false
          this.autoPickSingles()
          this.initFuzzySelection()
          this.emitIfReady(false)
        },
        fail: () => {
          this.confirmLoading = false
        },
      })
    },
    autoPickSingles() {
      const next = { ...(this.selectedSpecs || {}) }
      for (const f of this.specFields) {
        const values = f.styleValues || []
        if (values.length === 1) next[f.styleType] = values[0]
      }
      this.selectedSpecs = next
      this.updateMatchHint()
    },
    initFuzzySelection() {
      const next = {}
      for (const f of this.fuzzySpecFields) {
        next[f.styleType] = []
      }
      this.fuzzySelectedSpecs = next
      this.updateMatchHint()
    },
    handleSpecTagChange(checked, styleType, value) {
      const next = { ...(this.selectedSpecs || {}) }
      next[styleType] = checked ? value : undefined
      this.selectedSpecs = next
      this.updateMatchHint()
      // this.emitIfReady(false)
    },
    handleFuzzySpecTagChange(checked, styleType, value) {
      const current = [...(this.fuzzySelectedSpecs?.[styleType] || [])]
      let nextValues = current
      if (checked) {
        if (!current.includes(value)) nextValues = [...current, value]
      } else {
        nextValues = current.filter((x) => x !== value)
      }
      this.fuzzySelectedSpecs = {
        ...(this.fuzzySelectedSpecs || {}),
        [styleType]: nextValues,
      }
      this.updateMatchHint()
    },
    updateMatchHint() {
      if (!this.skuResult) {
        this.matchHint = ''
        return
      }
      if (this.matchMode === 'fuzzy') {
        const matched = this.findFuzzyMatchedSkuRelations()
        this.matchHint = matched.length
          ? `${this.$t('当前可匹配SKU数')}: ${matched.length}`
          : this.$t('当前条件下无可匹配SKU')
        return
      }
      const matched = this.findMatchedSkuRelations()
      if (!this.canSubmit) {
        this.matchHint = matched.length
          ? `${this.$t('当前可匹配SKU数')}: ${matched.length}`
          : this.$t('当前条件下无可匹配SKU')
        return
      }
      if (matched.length === 1) {
        this.matchHint = `${this.$t('已匹配到SKU')}: ${matched[0].skuCode}`
      } else if (matched.length === 0) {
        this.matchHint = this.$t('当前条件下无可匹配SKU')
      } else {
        this.matchHint = `${this.$t('匹配到多个SKU，请继续调整条件')} (${matched.length})`
      }
    },
    findMatchedSkuRelations() {
      const relations = this.skuResult?.skuRelations || []
      const selected = this.selectedSpecs || {}
      return relations.filter((r) => {
        const specs = r?.specs || []
        // 仅按已选择的字段过滤（支持未选齐时做 hint）
        return Object.keys(selected).every((styleType) => {
          const v = selected[styleType]
          if (!v) return true
          return specs.some((s) => s.styleType === styleType && s.styleValue === v)
        })
      })
    },
    findFuzzyMatchedSkuRelations() {
      const relations = this.skuResult?.skuRelations || []
      const selected = this.fuzzySelectedSpecs || {}
      return relations.filter((r) => {
        const specs = r?.specs || []
        return this.fuzzySpecFields.every((f) => {
          const selectedValues = selected[f.styleType] || []
          // 模糊查询规则：某个规格没选中任何值时，不作为过滤条件（即匹配所有）
          if (selectedValues.length === 0) return true
          const specItem = specs.find((s) => s.styleType === f.styleType)
          if (!specItem) return false
          return selectedValues.includes(specItem.styleValue)
        })
      })
    },
    queryFuzzyStockList({ isNewSearch = true } = {}) {
      if (isNewSearch) {
        this.fuzzySearchData.offset = 0
      }
      const matched = this.findFuzzyMatchedSkuRelations()
      if (!matched.length) {
        this.$message.warning(this.$t('当前条件下无可匹配SKU'))
        return
      }
      if (!this.warehouseCode) {
        this.$message.warning(this.$t('缺少仓库编码，无法查询库存'))
        return
      }
      const barcodeList = Array.from(
        new Set(
          matched
            .map((x) => x?.barcode || x?.skuCode)
            .filter((x) => x !== undefined && x !== null && `${x}`.trim() !== '')
            .map((x) => `${x}`)
        )
      )
      if (!barcodeList.length) {
        this.$message.warning(this.$t('未找到可查询库存的barcode'))
        return
      }

      // 仅在非分页操作时重置选中态，避免表格操作时闪烁/丢失
      if (isNewSearch) {
        this.resetFuzzyStockState()
      }
      this.confirmLoading = true
      http({
        type: 'post',
        url: api.stockListSimple,
        data: {
          barcodeList,
          warehouseCode: this.warehouseCode,
          pageNo: this.fuzzySearchData.offset / this.fuzzySearchData.length + 1,
          pageSize: this.fuzzySearchData.length,
        },
        hasLoading: false,
        success: (res) => {
          this.confirmLoading = false
          this.fuzzyStockResult = res?.result || {}
          this.fuzzyStockModalVisible = true
          this.fuzzySearchData.total = res?.result?.total || 0
          if (isNewSearch) {
            this.fuzzyStockSelectedRowKeys = []
            this.fuzzySelectedStockItem = null
          }
        },
        fail: () => {
          this.confirmLoading = false
        },
      })
    },
    paginate({ offset, length }) {
      this.fuzzySearchData.offset = offset
      this.fuzzySearchData.length = length
      this.queryFuzzyStockList({ isNewSearch: false })
    },
    handleFuzzyStockCancel() {
      this.fuzzyStockModalVisible = false
      this.fuzzyStockSelectedRowKeys = []
      this.fuzzySelectedStockItem = null
    },
    confirmFuzzySelection() {
      if (!this.fuzzySelectedStockItem) {
        this.$message.warning(this.$t('请选择一个SKU'))
        return
      }

      const chosenBarcode =
        this.fuzzySelectedStockItem?.barcode || this.fuzzySelectedStockItem?.skuCode || ''
      const relations = this.skuResult?.skuRelations || []
      const chosenRelation =
        relations.find((r) => r?.skuCode === chosenBarcode) ||
        relations.find((r) => r?.barcode === chosenBarcode) ||
        null

      const skuCode = chosenRelation?.skuCode || chosenBarcode
      if (!skuCode) {
        this.$message.warning(this.$t('无法定位所选SKU'))
        return
      }

      // 以 requiredStyleTypes 为准（排除版本）
      const selectedSpecs = {}
      for (const styleType of this.requiredStyleTypes) {
        const fromRelation = chosenRelation?.specs?.find(
          (s) => s.styleType === styleType
        )?.styleValue
        const fromStock = this.fuzzySelectedStockItem?.styleMap?.[styleType]
        selectedSpecs[styleType] = fromRelation || fromStock
      }

      const payload = {
        skuCode,
        skuId: chosenRelation?.skuId,
        goodsId: this.skuResult?.goodsId,
        goodsCode: this.skuResult?.goodsCode,
        goodsName: this.skuResult?.goodsName,
        brandName: this.skuResult?.brandName,
        modelName: this.skuResult?.modelName,
        selectedSpecs,
        raw: this.skuResult,
      }

      this.saveRecentPair({
        brandCode: this.form.brandCode,
        brandName: this.selectedBrandRaw?.brandName || this.skuResult?.brandName,
        modelCode: this.form.modelCode,
        modelName: this.selectedModelRaw?.modelName || this.skuResult?.modelName,
      })

      this.$emit('selected', payload)
      this.fuzzyStockModalVisible = false
      this.visible = false
    },
    emitIfReady(force) {
      if (!this.skuResult) return
      if (!force && !this.canSubmit) return
      const required = this.requiredStyleTypes

      const matched = this.findMatchedSkuRelations().filter((r) => {
        // force / canSubmit 都要求必选项齐全，额外再做一次严格校验
        return required.every((t) => {
          const v = this.selectedSpecs?.[t]
          return (r?.specs || []).some((s) => s.styleType === t && s.styleValue === v)
        })
      })

      if (matched.length !== 1) {
        this.updateMatchHint()
        if (force) this.$message.warning(this.$t('请先选择完整规格并确保唯一SKU'))
        return
      }

      const skuCode = matched[0].skuCode
      const payload = {
        skuCode,
        skuId: matched[0]?.skuId,
        goodsId: this.skuResult.goodsId,
        goodsCode: this.skuResult.goodsCode,
        goodsName: this.skuResult.goodsName,
        brandName: this.skuResult.brandName,
        modelName: this.skuResult.modelName,
        selectedSpecs: { ...(this.selectedSpecs || {}) },
        raw: this.skuResult,
      }
      this.saveRecentPair({
        brandCode: this.form.brandCode,
        brandName: this.selectedBrandRaw?.brandName || this.skuResult.brandName,
        modelCode: this.form.modelCode,
        modelName: this.selectedModelRaw?.modelName || this.skuResult.modelName,
      })
      this.$emit('selected', payload)
      this.visible = false
    },

    loadRecentPairs() {
      try {
        const raw = localStorage.getItem(RECENT_CACHE_KEY)
        const list = raw ? JSON.parse(raw) : []
        this.recentPairs = Array.isArray(list) ? list : []
      } catch (e) {
        this.recentPairs = []
      }
    },
    persistRecentPairs() {
      try {
        localStorage.setItem(RECENT_CACHE_KEY, JSON.stringify(this.recentPairs || []))
      } catch (e) {
        // ignore
      }
    },
    saveRecentPair({ brandCode, brandName, modelCode, modelName }) {
      if (!brandCode || !modelCode) return
      const key = `${brandCode}__${modelCode}`
      const now = Date.now()
      const next = [
        {
          key,
          brandCode,
          brandName: brandName || '',
          modelCode,
          modelName: modelName || '',
          ts: now,
        },
        ...(this.recentPairs || []).filter((x) => x && x.key !== key),
      ].slice(0, RECENT_MAX)
      this.recentPairs = next
      this.persistRecentPairs()
    },
    handleRemoveRecent(e, key) {
      // 防止 close 冒泡触发 click 选中
      try {
        e && e.preventDefault && e.preventDefault()
        e && e.stopPropagation && e.stopPropagation()
      } catch (err) {
        // ignore
      }
      this.recentPairs = (this.recentPairs || []).filter((x) => x && x.key !== key)
      this.persistRecentPairs()
    },
    applyRecent(item) {
      if (!item?.brandCode || !item?.modelCode) return
      this.fetchBrandList().then(() => {
        this.form.brandCode = item.brandCode
        this.handleBrandChange(item.brandCode)
        this.$nextTick(() => {
          this.form.modelCode = item.modelCode
          this.handleModelChange(item.modelCode)
        })
      })
    },
  },
}
</script>

<style scoped>
.recent-wrapper {
  display: flex;
  align-items: flex-start;
}
.recent-label {
  width: 84px;
  line-height: 32px;
  color: rgba(0, 0, 0, 0.65);
}
.recent-tags {
  flex: 1;
}
.recent-tags :deep(.ant-tag) {
  cursor: pointer;
  user-select: none;
}

.tag-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 4px;
}
.tag-group-scroll {
  max-height: 132px;
  overflow: auto;
  padding-right: 4px;
}

.cursor-pointer {
  cursor: pointer;
  user-select: none;
}
</style>
